import React, { useState, useMemo, useEffect } from 'react';

import { Box, Typography, Paper, Grid, Card, CardContent, CardActions, Button, Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Info as InfoIcon, Warning as WarningIcon, Error as ErrorIcon, Search as SearchIcon } from '@material-ui/icons';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import FormSelect from 'components/FormSelect';
import FormTextInput from 'components/FormTextInput';
import Loader from 'components/Loader';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { ActionTemplate, ActionTemplateListParams } from 'config/api/actionLibrary/actionLibrary.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useDebounce from 'hooks/useDebounce';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

import { ActionLibraryInstanceFormValues } from '../../ActionLibraryInstanceForm.types';

import useStyles from './styles/StepTemplateSelection.styles';

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const StepTemplateSelection: React.FC = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<ActionLibraryInstanceFormValues>();

  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState<string | number>('');
  const [subAreaFilter, setSubAreaFilter] = useState<string>('');

  const debouncedSearch = useDebounce(search, 500);

  // Fetch Areas
  const { data: areasData } = useQuery('ACTION_TEMPLATES_AREAS', actionTemplatesApi.getAreas);

  const areaOptions = useMemo(
    () =>
      areasData?.data.areas.map((area: any) => ({
        key: area.value,
        label: area.label,
      })) || [],
    [areasData],
  );

  const queryParams: ActionTemplateListParams = useMemo(() => {
    const params: ActionTemplateListParams = {
      limit: 100,
      status: 'active',
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    if (areaFilter) {
      params.area = areaFilter;
    }

    if (subAreaFilter) {
      params.sub_area = subAreaFilter;
    }

    return params;
  }, [debouncedSearch, areaFilter, subAreaFilter]);

  const { data: templatesData, isLoading: isLoadingTemplates } = useQuery(
    [QUERY_KEYS.ACTION_TEMPLATES_GET_ALL, queryParams],
    () => actionTemplatesApi.getAllActionTemplates(queryParams),
    { keepPreviousData: true },
  );

  const templates = templatesData?.data.results || [];

  // Derive Subareas from loaded templates
  const subAreaOptions = useMemo(() => {
    if (!areaFilter || !templates) return [];

    const uniqueSubAreas = new Map();

    templates.forEach((template: ActionTemplate) => {
      // Verify template belongs to selected area
      if (String(template.area) !== String(areaFilter)) return;

      if (template.subAreaData && template.subAreaData.id) {
        if (!uniqueSubAreas.has(template.subAreaData.id)) {
          uniqueSubAreas.set(template.subAreaData.id, {
            key: template.subAreaData.id,
            label: template.subAreaData.name || template.subAreaData.name,
          });
        }
      }
    });

    return Array.from(uniqueSubAreas.values());
  }, [templates, areaFilter]);

  // Reset subarea when area changes
  useEffect(() => {
    setSubAreaFilter('');
  }, [areaFilter]);

  const handleSelectTemplate = (template: ActionTemplate) => {
    setFieldValue('templateId', template.id.toString());
    setFieldValue('selectedTemplate', template);
    setFieldValue('instanceName', template.name);
    setFieldValue('reportedTo', template.reportedTo || '');
    setFieldValue('criticality', template.criticality || '');
    setFieldValue(
      'stepOverrides',
      (template.steps || []).map(step => ({
        originalStepId: step.id,
        included: true,
        mandatory: !!step.mandatory,
      })),
    );
  };

  const handleClearSelection = () => {
    setFieldValue('templateId', '');
    setFieldValue('selectedTemplate', null);
    setFieldValue('instanceName', '');
    setFieldValue('reportedTo', '');
    setFieldValue('criticality', '');
    setFieldValue('stepOverrides', []);
  };

  const renderCriticalityIcon = (criticality: string) => {
    let icon;
    let title = '';

    const criticalityKey = criticality.toUpperCase();

    const criticalityText = (level: 'HIGH' | 'MEDIUM' | 'LOW') =>
      `${t(...(action_library_messages.table.criticality as [string, string]))}: ${t(
        ...(action_library_messages.criticality[level] as [string, string]),
      )}`;

    if (criticalityKey === 'HIGH') {
      icon = <ErrorIcon color='error' fontSize='small' />;
      title = criticalityText('HIGH');
    } else if (criticalityKey === 'MEDIUM') {
      icon = <WarningIcon fontSize='small' style={{ color: '#ff9800' }} />;
      title = criticalityText('MEDIUM');
    } else {
      icon = <InfoIcon color='primary' fontSize='small' />;
      title = criticalityText('LOW');
    }

    return (
      <Tooltip title={title}>
        <Box alignItems='center' display='flex'>
          {icon}
        </Box>
      </Tooltip>
    );
  };

  const getCleanDescription = (htmlContent: string) => htmlContent.replace(/<[^>]+>/g, '');

  const renderTemplateInfoTooltip = (template: ActionTemplate) => (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography component='div' variant='caption'>
            <strong>{t(action_library_messages.table.purpose)}:</strong> {template.purposeDisplay}
          </Typography>
        </Grid>
        {(template.areaData || template.areaDisplay) && (
          <Grid item xs={6}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.table.area)}:</strong> {template.areaData?.displayName ?? template.areaDisplay}
            </Typography>
          </Grid>
        )}
        {template.subAreaData && (
          <Grid item xs={6}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.table.subarea)}:</strong> {template.subAreaData.name}
            </Typography>
          </Grid>
        )}
        <Grid item xs={6}>
          <Typography component='div' variant='caption'>
            <strong>{t(action_library_messages.instance.form.owner_type)}:</strong> {template.ownerTypeDisplay}
          </Typography>
        </Grid>
        {template.reference && (
          <Grid item xs={12}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.detail.reference)}:</strong> {template.reference}
            </Typography>
          </Grid>
        )}
        {template.reportedTo && (
          <Grid item xs={12}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.detail.reported_to)}:</strong> {template.reportedTo}
            </Typography>
          </Grid>
        )}
        {template.validityPeriodMonths && (
          <Grid item xs={12}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.instance.form.validity_period)}:</strong> {template.validityPeriodMonths}{' '}
              {template.validityPeriodMonths === 1
                ? t(action_library_messages.instance.form.month)
                : t(action_library_messages.instance.form.months)}
            </Typography>
          </Grid>
        )}
        {template.assetCategories && template.assetCategories.length > 0 && (
          <Grid item xs={12}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.instance.form.asset_categories)}:</strong>{' '}
              {template.assetCategories.map(ac => ac.name).join(', ')}
            </Typography>
          </Grid>
        )}
        {template.tags && Object.keys(template.tags).length > 0 && (
          <Grid item xs={12}>
            <Typography component='div' variant='caption'>
              <strong>{t(general_messages.tags)}:</strong> {Object.values(template.tags).join(', ')}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  if (values.selectedTemplate) {
    const template = values.selectedTemplate;
    return (
      <Box>
        <Paper className={styles.selectedSummary}>
          <Box alignItems='center' display='flex' justifyContent='space-between'>
            <Box>
              <Typography color='textSecondary' variant='subtitle2'>
                {t(action_library_messages.instance.form.template_selected)}
              </Typography>
              <Box alignItems='center' display='flex' mt={1}>
                <Typography style={{ marginRight: 8 }} variant='h6'>
                  {template.name}
                </Typography>
                {renderCriticalityIcon(template.criticality)}
              </Box>
              <Typography color='textSecondary' variant='body2'>
                {template.areaData?.displayName ?? template.areaDisplay}
                {template.subAreaData ? ` • ${template.subAreaData.name}` : ''} •{' '}
                {t(action_library_messages.instance.form.steps_count, { count: template.steps?.length || 0 })}
              </Typography>
            </Box>
            <Button color='primary' onClick={handleClearSelection} variant='outlined'>
              {t(action_library_messages.instance.form.change_template)}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box className={styles.filtersContainer}>
        <Grid container spacing={2}>
          <Grid item md={4} xs={12}>
            {/* We mock formik for filters since they are local state */}
            <FormSelect
              className={styles.fullWidth}
              formik={
                {
                  values: { area: areaFilter },
                  touched: {},
                  errors: {},
                  handleChange: () => (e: React.ChangeEvent<{ value: unknown }>) => setAreaFilter(e.target.value as string | number),
                  handleBlur: () => {},
                  setFieldTouched: () => {},
                } as any
              }
              id='area'
              label={t(action_library_messages.filters.area)}
              options={[{ key: '', label: t(action_library_messages.instance.form.all_areas) }, ...areaOptions]}
            />
          </Grid>

          {subAreaOptions.length > 0 && (
            <Grid item md={4} xs={12}>
              <FormSelect
                className={styles.fullWidth}
                formik={
                  {
                    values: { subArea: subAreaFilter },
                    touched: {},
                    errors: {},
                    handleChange: () => (e: React.ChangeEvent<{ value: unknown }>) => setSubAreaFilter(e.target.value as string),
                    handleBlur: () => {},
                    setFieldTouched: () => {},
                  } as any
                }
                id='subArea'
                label={t(action_library_messages.filters.subarea)}
                options={[{ key: '', label: t(action_library_messages.instance.form.all_subareas) }, ...subAreaOptions]}
              />
            </Grid>
          )}

          <Grid item md={subAreaOptions.length > 0 ? 4 : 8} xs={12}>
            <FormTextInput
              className={styles.fullWidth}
              formik={
                {
                  values: { search },
                  touched: {},
                  errors: {},
                  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
                  handleBlur: () => {},
                  setFieldTouched: () => {},
                } as any
              }
              id='search'
              label={t(action_library_messages.instance.form.search_placeholder)}
            />
          </Grid>
        </Grid>
      </Box>

      {isLoadingTemplates ? (
        <Loader />
      ) : (
        <Grid container spacing={2}>
          {templates.length === 0 ? (
            <Grid item xs={12}>
              <Typography align='center' color='textSecondary'>
                {t(action_library_messages.instance.form.no_templates_found)}
              </Typography>
            </Grid>
          ) : (
            templates.map((template: ActionTemplate) => (
              <Grid key={template.id} item md={4} sm={6} xs={12}>
                <Card
                  className={`${styles.templateCard} ${values.templateId === template.id.toString() ? styles.selectedCard : ''}`}
                  onClick={() => handleSelectTemplate(template)}
                >
                  <CardContent>
                    <Box className={styles.cardHeader}>
                      <Typography className={styles.cardTitle} variant='subtitle1'>
                        {template.name}
                      </Typography>
                      <Box alignItems='center' display='flex'>
                        <HtmlTooltip interactive placement='top' title={renderTemplateInfoTooltip(template)}>
                          <Box
                            alignItems='center'
                            display='flex'
                            onClick={e => e.stopPropagation()}
                            style={{ cursor: 'pointer', marginRight: 8 }}
                          >
                            <SearchIcon color='action' fontSize='small' />
                          </Box>
                        </HtmlTooltip>
                        {renderCriticalityIcon(template.criticality)}
                      </Box>
                    </Box>
                    <Typography color='textSecondary' gutterBottom variant='body2'>
                      {template.areaDisplay}
                    </Typography>
                    <Typography className={styles.description} variant='body2'>
                      {getCleanDescription(template.shortDescription)}
                    </Typography>
                    <Typography noWrap variant='body2'>
                      {t(action_library_messages.instance.form.steps_count, { count: template.steps?.length || 0 })}
                    </Typography>
                    {/* Tags would go here if available in list view properly */}
                  </CardContent>
                  <CardActions>
                    <Button color='secondary' onClick={() => handleSelectTemplate(template)} size='small'>
                      {t(action_library_messages.instance.form.select_template_button)}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}
    </Box>
  );
};

export default StepTemplateSelection;
